import type { ReactNode } from 'react';

export type StKpiTone = 'brand' | 'indigo' | 'success' | 'violet' | 'teal' | 'warning' | 'danger';

const TONE_MAP: Record<StKpiTone, { tint: string; iconBg: string; halo: string }> = {
  brand:   { tint: 'bg-brand-50/80 dark:bg-brand-900/20',     iconBg: 'bg-gradient-to-br from-brand-500 to-brand-600',     halo: 'bg-gradient-to-br from-brand-500 to-brand-700' },
  indigo:  { tint: 'bg-indigo-50/80 dark:bg-indigo-900/20',   iconBg: 'bg-gradient-to-br from-indigo-500 to-indigo-600',   halo: 'bg-gradient-to-br from-indigo-500 to-indigo-700' },
  success: { tint: 'bg-success-50/80 dark:bg-success-900/20', iconBg: 'bg-gradient-to-br from-success-500 to-success-600', halo: 'bg-gradient-to-br from-success-500 to-success-700' },
  violet:  { tint: 'bg-violet-50/80 dark:bg-violet-900/20',   iconBg: 'bg-gradient-to-br from-violet-500 to-violet-600',   halo: 'bg-gradient-to-br from-violet-500 to-violet-700' },
  teal:    { tint: 'bg-teal-50/80 dark:bg-teal-900/20',       iconBg: 'bg-gradient-to-br from-teal-500 to-teal-600',       halo: 'bg-gradient-to-br from-teal-500 to-teal-700' },
  warning: { tint: 'bg-warning-50/80 dark:bg-warning-900/20', iconBg: 'bg-gradient-to-br from-warning-500 to-warning-600', halo: 'bg-gradient-to-br from-warning-500 to-warning-700' },
  danger:  { tint: 'bg-danger-50/80 dark:bg-danger-900/20',   iconBg: 'bg-gradient-to-br from-danger-500 to-danger-600',   halo: 'bg-gradient-to-br from-danger-500 to-danger-700' },
};

interface StKpiProps {
  title: string;
  value: string;
  icon: ReactNode;
  tone: StKpiTone;
  subtext?: string;
  subtextTone?: 'success' | 'warning' | 'danger' | 'neutral';
  delay?: number;
  masked?: boolean;
}

export function StKpi({
  title,
  value,
  icon,
  tone,
  subtext,
  subtextTone = 'neutral',
  delay,
  masked = false,
}: StKpiProps) {
  const t = TONE_MAP[tone];
  const subtextClass =
    subtextTone === 'success'
      ? 'text-success-700 dark:text-success-300'
      : subtextTone === 'warning'
        ? 'text-warning-700 dark:text-warning-300'
        : subtextTone === 'danger'
          ? 'text-danger-700 dark:text-danger-300'
          : 'text-neutral-500 dark:text-neutral-400';

  return (
    <div
      className={`relative overflow-hidden rounded-2xl ${t.tint} backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)] hover:shadow-[0_18px_40px_-18px_rgba(15,23,42,0.28)] transition-all duration-200 p-5 animate-slide-up`}
      style={delay ? { animationDelay: `${delay}ms` } : undefined}
    >
      <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full ${t.halo} opacity-15`} aria-hidden="true" />
      <div className="relative flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl ${t.iconBg} flex items-center justify-center text-white shadow-[0_6px_16px_-8px_rgba(15,23,42,0.5)]`}>
          {icon}
        </div>
        <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
          {title}
        </span>
      </div>
      <div className="relative">
        <p className={`font-mono text-2xl font-bold tabular-nums leading-none ${masked ? 'text-neutral-400 dark:text-neutral-600' : 'text-neutral-900 dark:text-white'}`}>
          {value}
        </p>
        {subtext && (
          <p className={`text-xs mt-2 font-medium tabular-nums ${subtextClass}`}>{subtext}</p>
        )}
      </div>
    </div>
  );
}
